import {
  addNumberOrdinal,
  formatTimestamp,
  getDayName,
  getMonthName,
} from "common/helpers/utils";
import type { ConversationParticipant, DetailedLead } from "common/types";

import { MIME_TYPES } from "./constants";

export function formatConversationMessagesDate(date: Date) {
  const dateNow = new Date();
  const dateYesterday = new Date();
  dateYesterday.setDate(dateNow.getDate() - 1);

  // Calculate the difference in days between the current date and the message date
  const diffDays = Math.floor(
    (dateNow.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (
    date.getFullYear() === dateNow.getFullYear() &&
    date.getMonth() === dateNow.getMonth() &&
    date.getDate() === dateNow.getDate()
  ) {
    return `Today, ${formatTimestamp(date.getTime())}`;
  }

  if (
    date.getFullYear() === dateYesterday.getFullYear() &&
    date.getMonth() === dateYesterday.getMonth() &&
    date.getDate() === dateYesterday.getDate()
  ) {
    return `Yesterday, ${formatTimestamp(date.getTime())}`;
  }

  if (diffDays >= 2 && diffDays <= 7) {
    return `${getDayName(date.getDay())}, ${formatTimestamp(date.getTime())}`;
  }

  return `${getMonthName(date.getMonth())} ${addNumberOrdinal(date.getDate())}${dateNow.getFullYear() !== date.getFullYear() ? ` ${date.getFullYear()}` : ""}, ${formatTimestamp(date.getTime())}`;
}

export function getGroupParticipantsString(
  participants: ConversationParticipant[],
) {
  const participantCount = participants.length;

  if (participantCount > 2) {
    const firstTwo = participants
      .slice(0, 2)
      .map(({ full_name }) => `${full_name}`)
      .join(", ");
    const remainingCount = participantCount - 2;
    return `${firstTwo} + ${remainingCount} more`;
  }

  return participants.map(({ full_name }) => `${full_name}`).join(", ");
}

export function getParticipantJobInfo(lead: DetailedLead) {
  const { current_experience: currentExperience, occupation } = lead;

  if (currentExperience && currentExperience.length > 0) {
    const firstExperience = currentExperience[0];
    const { job_title: jobTitle, company } = firstExperience;

    if (jobTitle && company?.name) {
      return `${jobTitle} · ${company.name}`;
    }

    if (jobTitle) {
      return jobTitle;
    }
  }
  return occupation;
}

export function formatChatTimestamp(unixTimestamp: number) {
  const today = new Date();

  // Set to the start of today
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);

  // Set to the start of yesterday
  yesterday.setDate(today.getDate() - 1);

  const date = new Date(unixTimestamp);

  if (unixTimestamp >= today.getTime()) {
    // Get hours and minutes for today's messages
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12; // Convert to 12-hour format
    return `${hours}:${minutes} ${ampm}`;
  }

  if (unixTimestamp >= yesterday.getTime()) {
    return "Yesterday";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

export function generateAudioFilename(mimeType: string) {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);

  const matchedMimeType = MIME_TYPES.find((typeObj) =>
    mimeType.includes(typeObj.mimeType.split(";")[0]),
  );
  const extension = matchedMimeType ? matchedMimeType.extension : "wav";

  return `audio-${timestamp}.${extension}`;
}

export function isBlobUrl(url: string) {
  return typeof url === "string" && url.startsWith("blob:");
}

export function stripTextHighlight(text: string) {
  return text.replace(/<em>|<\/em>/g, "");
}
